import { useState } from 'react';
import { Button, Card } from '@lms/ui';
import { X, Layers } from 'lucide-react';
import { useCreateLevel, useUpdateLevel } from '../hooks/usePrograms';
import type { ProgramLevel } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface LevelModalProps {
  programId: string;
  order: number;
  level?: ProgramLevel;
  onClose: () => void;
}

export function LevelModal({ programId, order, level, onClose }: LevelModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState(level?.name || '');
  const [description, setDescription] = useState(level?.description || '');
  const [isSequential, setIsSequential] = useState(level?.isSequential || false);
  const createLevelMutation = useCreateLevel();
  const updateLevelMutation = useUpdateLevel();

  const isEditing = !!level;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    if (isEditing) {
      updateLevelMutation.mutate(
        {
          programId,
          levelId: level.id,
          data: {
            name,
            description,
            order,
            isSequential,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      createLevelMutation.mutate(
        {
          programId,
          data: {
            name,
            description,
            order,
            isSequential,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <Card className="w-full max-w-md !p-0 overflow-hidden shadow-level5 border border-on-surface/5 animate-scaleIn my-auto">
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Layers size={20} />
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                <Bilingual
                  {...(isEditing ? copy.levelModal.titleEdit : copy.levelModal.titleCreate)}
                />
              </h3>
              <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                {t(
                  isEditing ? copy.levelModal.subtitleEdit : copy.levelModal.subtitleCreate
                ).replace('{order}', String(order).padStart(2, '0'))}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-container-high rounded-full transition-colors text-on-surface-variant"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="level-name"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.levelModal.labelName} />
              </label>
              <input
                id="level-name"
                autoFocus
                className="w-full bg-surface-container-low border-none rounded-xl px-5 py-4 text-base font-body focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                placeholder={t(copy.levelModal.placeholderName)}
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="level-desc"
                className="text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.levelModal.labelDesc} />
              </label>
              <textarea
                id="level-desc"
                className="w-full bg-surface-container-low border-none rounded-xl px-5 py-4 text-sm font-body focus:ring-2 focus:ring-primary/20 transition-all outline-none min-h-[100px] resize-none"
                placeholder={t(copy.levelModal.placeholderDesc)}
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between bg-surface-container-low/50 p-5 rounded-2xl border border-on-surface/5">
              <div className="space-y-0.5">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                  <Bilingual {...copy.levelModal.labelAccessModel} />
                </p>
                <p className="text-xs font-bold text-on-surface">
                  <Bilingual {...copy.levelModal.sequentialLabel} />
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsSequential(!isSequential)}
                className={`w-12 h-6 rounded-full transition-all relative ${isSequential ? 'bg-primary' : 'bg-on-surface-variant/20'}`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isSequential ? 'left-7' : 'left-1'}`}
                />
              </button>
            </div>
          </div>

          <div className="pt-4 flex gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1 h-12 rounded-xl text-[10px] font-black uppercase tracking-widest"
            >
              <Bilingual {...copy.levelModal.abort} />
            </Button>
            <Button
              type="submit"
              disabled={createLevelMutation.isPending || !name}
              className="flex-[2] h-12 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-level2"
            >
              {createLevelMutation.isPending || updateLevelMutation.isPending
                ? t(copy.levelModal.submitPending)
                : isEditing
                  ? t(copy.levelModal.submitEdit)
                  : t(copy.levelModal.submitCreate)}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
